import React, { useState } from "react";
import * as Phosphor from "@phosphor-icons/react";
import { CheckCircle, ArrowRight, Handshake, LockKey, ChatCircleText, Repeat } from "@phosphor-icons/react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { Overline, Container, btn } from "@/components/ui-kit";
import { Field, TextInput, TextArea, NativeSelect } from "@/components/forms";
import FileUpload from "@/components/FileUpload";
import AntiSpam from "@/components/AntiSpam";
import { api } from "@/lib/api";
import { IMAGES } from "@/lib/content";
import { toast } from "sonner";

const Icon = ({ name, ...props }) => {
  const C = Phosphor[name] || Phosphor.Circle;
  return <C {...props} />;
};

const audience = [
  "Land brokers", "Commercial brokers", "Residential Realtors", "Builders", "Developers",
  "Civil engineers", "Architects", "Land planners", "Attorneys", "Investment partners",
];

const OPPORTUNITIES = [
  { icon: "Tag", title: "Listed Properties", desc: "Active listings that fit our residential development focus." },
  { icon: "EyeSlash", title: "Off-Market Properties", desc: "Quiet, confidential situations before they reach the market." },
  { icon: "MapTrifold", title: "Development Land", desc: "Well-located parcels with residential development potential." },
  { icon: "Intersect", title: "Assemblages", desc: "Adjacent parcels that create greater combined value." },
  { icon: "Scroll", title: "Estate Properties", desc: "Legacy and family-owned land ready for a new chapter." },
  { icon: "Gavel", title: "Entitlement & Rezoning", desc: "Properties whose value can be unlocked through approvals." },
  { icon: "Buildings", title: "Builder Opportunities", desc: "Finished-lot and joint-venture opportunities for builders." },
];

const PRINCIPLES = [
  { icon: ChatCircleText, title: "Responsive & Clear", desc: "We review every opportunity promptly and communicate honestly about fit and next steps." },
  { icon: LockKey, title: "Discreet", desc: "Off-market and sensitive situations are handled with confidentiality and care." },
  { icon: Handshake, title: "Fair Dealing", desc: "We favor straightforward, good-faith negotiations and respect existing relationships." },
  { icon: Repeat, title: "Long-Term Relationships", desc: "We value repeat partners over one-off transactions — and want to be your first call." },
];

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export default function BrokersPartners() {
  const [files, setFiles] = useState([]);
  const [spam, setSpam] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    broker_name: "", company: "", email: "", phone: "",
    property_address: "", parcel_id: "", county: "", acreage: "",
    asking_price: "", current_zoning: "", water_availability: "", sewer_availability: "",
    property_description: "", listing_url: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const err = {};
    if (!form.broker_name.trim()) err.broker_name = "Required.";
    if (!emailOk(form.email)) err.email = "Valid email required.";
    if (!form.phone.trim()) err.phone = "Required.";
    setErrors(err);
    if (Object.keys(err).length) return;
    if (!spam.captcha_answer || spam.captcha_answer !== spam.captcha_a + spam.captcha_b) {
      toast.error("Please answer the verification question correctly.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/submissions/broker", { ...form, files, ...spam });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="brokers-partners-page">
      <Seo
        title="Brokers & Partners — Bring Us Land Opportunities"
        description="Redwood Capital builds long-term relationships with brokers, Realtors, builders, engineers, and developers. Bring us listed or off-market land, development parcels, assemblages, estate properties, and builder opportunities across the Charlotte region."
        path="/brokers-partners"
      />
      <PageHero
        overline="Brokers & Partners"
        title="Let's Find Opportunities Together"
        subtitle="We value long-term relationships with the professionals who identify land and development opportunities. Bring us a deal — listed or off-market — and we'll give it a prompt, professional review."
        image={IMAGES.meeting}
      />

      {/* RELATIONSHIP INTRO + AUDIENCE */}
      <section className="py-20 md:py-28" data-testid="who-we-work-with">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
            <Reveal className="lg:col-span-6">
              <Overline>Who We Work With</Overline>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold text-forest tracking-tight">
                Relationships that create opportunities
              </h2>
              <p className="mt-6 text-ink text-lg leading-relaxed">
                Great development starts with great relationships. We work alongside the brokers, Realtors, builders,
                and consultants who know their markets and their clients — and we aim to be a reliable, long-term
                partner they're glad to bring opportunities to.
              </p>
              <p className="mt-4 text-ink text-lg leading-relaxed">
                If you represent a client considering a sale, or simply know of land with potential, we'd like to
                connect.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-6">
              <div className="bg-white border border-line rounded-sm p-8">
                <p className="text-xs font-bold uppercase tracking-wider text-gold mb-4">Professionals we partner with</p>
                <ul className="flex flex-wrap gap-2.5">
                  {audience.map((a) => (
                    <li key={a} className="bg-cream border border-line rounded-sm px-4 py-2 text-sm text-forest font-medium">{a}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* OPPORTUNITIES WE'RE LOOKING FOR */}
      <section className="bg-white border-y border-line py-20 md:py-28" data-testid="opportunities">
        <Container>
          <Reveal>
            <Overline>Opportunities We're Looking For</Overline>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-forest tracking-tight max-w-2xl">
              Bring us the right kind of deal
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {OPPORTUNITIES.map((o, i) => (
              <Reveal key={o.title} delay={(i % 3) * 0.08}>
                <div className="h-full bg-cream border border-line rounded-sm p-8 transition-all duration-300 hover:border-gold hover:shadow-[0_14px_36px_rgba(13,46,27,0.08)] hover:-translate-y-1 group">
                  <div className="w-12 h-12 flex items-center justify-center rounded-sm bg-forest/5 group-hover:bg-gold/15 transition-colors duration-300">
                    <Icon name={o.icon} size={26} weight="light" className="text-forest" />
                  </div>
                  <h3 className="mt-6 font-serif text-xl font-semibold text-forest">{o.title}</h3>
                  <p className="mt-3 text-ink leading-relaxed text-[15px]">{o.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* HOW WE WORK — relationship principles */}
      <section className="relative bg-forest text-white py-20 md:py-28 overflow-hidden" data-testid="how-we-work">
        <img src={IMAGES.forest} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-[0.1]" />
        <div className="grain" />
        <Container className="relative">
          <Reveal>
            <Overline>How We Work With Partners</Overline>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold tracking-tight max-w-2xl">
              A partner you'll want to call again
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10">
            {PRINCIPLES.map((p, i) => {
              const IconC = p.icon;
              return (
                <Reveal key={p.title} delay={(i % 2) * 0.1}>
                  <div className="flex gap-5">
                    <div className="shrink-0 w-14 h-14 flex items-center justify-center rounded-sm bg-white/5 border border-white/10">
                      <IconC size={26} weight="light" className="text-gold" />
                    </div>
                    <div>
                      <h3 className="font-serif text-xl font-semibold text-white">{p.title}</h3>
                      <p className="mt-2 text-white/65 leading-relaxed">{p.desc}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      {/* HAVE A DEAL FOR US — form */}
      <section className="py-20 md:py-28" data-testid="deal-form-section">
        <Container>
          <div id="deal" className="max-w-3xl mx-auto">
            {done ? (
              <div className="bg-white border border-line rounded-sm p-10 text-center" data-testid="broker-success">
                <div className="mx-auto w-16 h-16 rounded-full bg-gold/15 flex items-center justify-center">
                  <CheckCircle size={40} weight="light" className="text-gold" />
                </div>
                <h3 className="mt-6 font-serif text-2xl font-semibold text-forest">Thank you</h3>
                <p className="mt-3 text-ink">We've received your opportunity and will follow up promptly if it's a fit.</p>
              </div>
            ) : (
              <>
                <Reveal>
                  <div className="text-center mb-10">
                    <Overline>Submit an Opportunity</Overline>
                    <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-forest tracking-tight">
                      Have a Deal for Us?
                    </h2>
                    <p className="mt-4 text-ink text-lg leading-relaxed max-w-xl mx-auto">
                      Share the essentials below. Every submission is reviewed personally and kept confidential.
                    </p>
                  </div>
                </Reveal>
                <Reveal delay={0.1}>
                  <form onSubmit={submit} className="bg-white border border-line rounded-sm p-6 sm:p-10 shadow-[0_12px_40px_rgba(13,46,27,0.08)] space-y-6" data-testid="broker-form">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <Field label="Name" required error={errors.broker_name}>
                        <TextInput value={form.broker_name} onChange={set("broker_name")} error={errors.broker_name} data-testid="broker-name" />
                      </Field>
                      <Field label="Company"><TextInput value={form.company} onChange={set("company")} data-testid="broker-company" /></Field>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <Field label="Email" required error={errors.email}>
                        <TextInput type="email" value={form.email} onChange={set("email")} error={errors.email} data-testid="broker-email" />
                      </Field>
                      <Field label="Phone" required error={errors.phone}>
                        <TextInput value={form.phone} onChange={set("phone")} error={errors.phone} data-testid="broker-phone" />
                      </Field>
                    </div>
                    <Field label="Property Address"><TextInput value={form.property_address} onChange={set("property_address")} data-testid="broker-address" /></Field>
                    <div className="grid sm:grid-cols-3 gap-6">
                      <Field label="County"><TextInput value={form.county} onChange={set("county")} data-testid="broker-county" /></Field>
                      <Field label="Parcel ID"><TextInput value={form.parcel_id} onChange={set("parcel_id")} data-testid="broker-parcel" /></Field>
                      <Field label="Acreage"><TextInput value={form.acreage} onChange={set("acreage")} data-testid="broker-acreage" /></Field>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <Field label="Asking Price"><TextInput value={form.asking_price} onChange={set("asking_price")} placeholder="Optional" data-testid="broker-price" /></Field>
                      <Field label="Current Zoning"><TextInput value={form.current_zoning} onChange={set("current_zoning")} data-testid="broker-zoning" /></Field>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <Field label="Water">
                        <NativeSelect value={form.water_availability} onChange={set("water_availability")} data-testid="broker-water">
                          <option value="">Select…</option>
                          <option>Available</option>
                          <option>Not Available</option>
                          <option>Unknown</option>
                        </NativeSelect>
                      </Field>
                      <Field label="Sewer">
                        <NativeSelect value={form.sewer_availability} onChange={set("sewer_availability")} data-testid="broker-sewer">
                          <option value="">Select…</option>
                          <option>Available</option>
                          <option>Not Available</option>
                          <option>Unknown</option>
                        </NativeSelect>
                      </Field>
                    </div>
                    <Field label="Property Description"><TextArea value={form.property_description} onChange={set("property_description")} placeholder="Location, potential, current status, and anything else worth noting." data-testid="broker-description" /></Field>
                    <Field label="Listing URL"><TextInput value={form.listing_url} onChange={set("listing_url")} placeholder="https://" data-testid="broker-url" /></Field>
                    <FileUpload testid="broker-files" category="broker" label="Document Upload — Offering Memorandum / Survey / Site Plan" value={files} onChange={setFiles} />
                    <AntiSpam onChange={setSpam} testid="broker-antispam" />
                    <button type="submit" disabled={submitting} className={`${btn.gold} w-full disabled:opacity-60`} data-testid="broker-submit">
                      {submitting ? "Submitting…" : "Submit an Opportunity"} {!submitting && <ArrowRight size={18} />}
                    </button>
                  </form>
                </Reveal>
              </>
            )}
          </div>
        </Container>
      </section>
    </div>
  );
}
